/**
 * VORAQEE custom listbox.
 *
 * Replaces native <select> widgets, which can't be styled consistently across
 * platforms. Drives any markup shaped like:
 *
 *   <div data-vq-select>
 *     <button data-vq-select-trigger aria-expanded="false">
 *       <span data-vq-select-label>Placeholder</span>
 *     </button>
 *     <div role="listbox" data-vq-select-panel hidden>
 *       <button role="option" data-value="IN">India</button>
 *     </div>
 *     <input type="hidden" data-vq-select-value>
 *   </div>
 *
 * Selecting an option writes to the hidden input and fires a `change` event on
 * it, so callers can listen the same way they would to a real select.
 *
 * Escape is handled on the capture phase so an open listbox swallows the key
 * before any ancestor dialog (the region gate) can act on it.
 */
(function () {
  'use strict';

  var OPEN_ATTR = 'data-open';

  function options(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[role="option"]'));
  }

  function close(root) {
    var panel = root.querySelector('[data-vq-select-panel]');
    var trigger = root.querySelector('[data-vq-select-trigger]');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    root.removeAttribute(OPEN_ATTR);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vq-select]'), function (root) {
      if (root !== except) close(root);
    });
  }

  function open(root) {
    var panel = root.querySelector('[data-vq-select-panel]');
    var trigger = root.querySelector('[data-vq-select-trigger]');
    if (!panel) return;
    closeAll(root);
    panel.hidden = false;
    root.setAttribute(OPEN_ATTR, 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var current = root.querySelector('[role="option"][aria-selected="true"]') || options(root)[0];
    if (current) current.focus();
  }

  function isOpen(root) {
    var panel = root.querySelector('[data-vq-select-panel]');
    return !!panel && !panel.hidden;
  }

  function select(root, option) {
    var label = root.querySelector('[data-vq-select-label]');
    var value = root.querySelector('[data-vq-select-value]');
    var trigger = root.querySelector('[data-vq-select-trigger]');

    options(root).forEach(function (el) {
      el.setAttribute('aria-selected', el === option ? 'true' : 'false');
    });

    // The first span holds the human-readable name; trailing spans are meta
    // (currency code), which shouldn't leak into the trigger label.
    var text = option.querySelector('span')
      ? option.querySelector('span').textContent.trim()
      : option.textContent.trim();

    if (label) label.textContent = text;
    if (value) {
      value.value = option.getAttribute('data-value') || '';
      value.dispatchEvent(new Event('change', { bubbles: true }));
    }

    close(root);
    if (trigger) trigger.focus();
  }

  function move(root, from, delta) {
    var list = options(root);
    if (!list.length) return;
    var index = list.indexOf(from);
    var next = index < 0 ? 0 : index + delta;
    if (next < 0) next = list.length - 1;
    if (next >= list.length) next = 0;
    list[next].focus();
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-vq-select-trigger]');
    if (trigger) {
      var root = trigger.closest('[data-vq-select]');
      if (!root) return;
      if (isOpen(root)) close(root);
      else open(root);
      return;
    }

    var option = event.target.closest('[role="option"]');
    if (option) {
      var owner = option.closest('[data-vq-select]');
      if (owner) {
        select(owner, option);
        return;
      }
    }

    // Any click outside an open listbox dismisses it.
    if (!event.target.closest('[data-vq-select]')) closeAll(null);
  });

  document.addEventListener(
    'keydown',
    function (event) {
      var root = event.target.closest ? event.target.closest('[data-vq-select]') : null;
      if (!root) return;

      var open_ = isOpen(root);

      if (event.key === 'Escape' && open_) {
        // Swallow it so an ancestor dialog doesn't close too.
        event.stopPropagation();
        event.preventDefault();
        close(root);
        var trigger = root.querySelector('[data-vq-select-trigger]');
        if (trigger) trigger.focus();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open_) {
          open(root);
          return;
        }
        move(root, event.target.closest('[role="option"]'), event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if ((event.key === 'Enter' || event.key === ' ') && open_) {
        var option = event.target.closest('[role="option"]');
        if (option) {
          event.preventDefault();
          select(root, option);
        }
      }
    },
    true
  );
})();
